import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import MuiDrawer from '@mui/material/Drawer';
import i18next from 'i18next';
import { Stack } from '@famly/mf_layout_stack';
import { Text } from '@famly/mf_data-display_text';
import { Avatar } from '@famly/mf_data-display_avatar';
import { useDataProps } from '@famly/mf_ts-utils_data-aria-props';
import { useBreakpoints } from '@famly/mf_util_breakpoints';
import { useKeyboardOverlap } from '@famly/mf_util_keyboard-overlap';
import { Button } from '@famly/mf_input_button';
import { Icon } from '@famly/mf_data-display_icon';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { DrawerTestAttributes } from './drawer-test-attributes';
import { useIsFloatingPopupOpen, useRegisterSideDrawer } from './side-drawer-registry';
const InnerDrawer = ({ children, onOpen, onClose, open, height, borderBottomLeftRadius = '0', borderBottomRightRadius = '0', borderTopLeftRadius = '0', borderTopRightRadius = '0', disablePortal, aboveModals = false, ...rest }) => {
    const dataProps = useDataProps(rest);
    const { isTabletPortraitAndLarger, isTabletLandscapeAndLarger } = useBreakpoints();
    /**
     * The drawer sits on the bottom edge of the layout viewport, which the on-screen keyboard does not
     * reliably shrink. Left alone, its lower part — where an input and its submit button usually live —
     * ends up underneath the keyboard, out of reach.
     */
    const { overlap, visibleHeight } = useKeyboardOverlap();
    /**
     * Empty until the viewport has been measured, and on any browser without a visual viewport.
     * Measured, never `vh`: that keeps resolving against the pre-keyboard height. border-box keeps the
     * drawer's safe-area padding inside the cap rather than adding to it. A lifted drawer drops that
     * padding: the keyboard, not the drawer, now sits over the home indicator.
     */
    const keyboardGeometry = visibleHeight > 0
        ? {
            bottom: `${overlap}px`,
            boxSizing: 'border-box',
            maxHeight: `calc(${visibleHeight}px - env(safe-area-inset-top))`,
            ...(overlap > 0 ? { paddingBottom: 0 } : {}),
        }
        : {};
    /**
     * Ensures that the drawer doesn't take up full width on tablets.
     * Note that the order of the if statements matter!
     */
    const maxWidth = React.useMemo(() => {
        if (isTabletLandscapeAndLarger) {
            return '70%';
        }
        if (isTabletPortraitAndLarger) {
            return '80%';
        }
        return '100%';
    }, [isTabletPortraitAndLarger, isTabletLandscapeAndLarger]);
    const PaperProps = {
        sx: {
            height: height || '100vh',
            borderBottomLeftRadius,
            borderBottomRightRadius,
            borderTopLeftRadius,
            borderTopRightRadius,
            paddingBottom: 'env(safe-area-inset-bottom)',
            maxWidth,
            margin: '0 auto',
            ...keyboardGeometry,
        },
    };
    return (_jsx(SwipeableDrawer, { anchor: "bottom", open: open, onClose: onClose, onOpen: onOpen, disablePortal: disablePortal, 
        // MUI's swipe-to-open hijacks touch events along the screen edges,
        // which makes adjacent UI (notably the mobile tab bar) feel unreliable
        // — a slight finger drift while tapping a tab would get swallowed.
        // No callsite opens a Sheet only via swipe, so we opt out everywhere.
        disableSwipeToOpen: true, PaperProps: PaperProps, 
        // Exactly the modal layer: menus opened from the sheet sit there too and, mounting later, stay in front.
        sx: aboveModals ? theme => ({ zIndex: theme.zIndex.modal }) : undefined, ...dataProps, children: children }));
};
const Puller = () => {
    return (_jsx(Box, { sx: theme => {
            return {
                width: 60,
                height: 6,
                backgroundColor: theme.modernFamlyTheme.colorPalette.n300,
                borderRadius: 3,
                position: 'absolute',
                top: 8,
                left: 'calc(50% - 30px)',
            };
        } }));
};
export const Sheet = ({ showPuller = true, children, height = '90vh', borderRadius = '16px', disablePortal = false, ...rest }) => {
    return (_jsxs(InnerDrawer, { height: height, ...rest, borderTopLeftRadius: borderRadius, borderTopRightRadius: borderRadius, disablePortal: disablePortal, children: [showPuller ? _jsx(Puller, {}) : null, children] }));
};
function isAvatarTitle(candidate) {
    return hasValue(candidate.avatar);
}
/**
 * This component renders the Drawer's title section with either only text, text and icon,
 * or an avatar component.
 *
 * Only exposed for testing, do not use directly.
 *
 * @param props containing the title to be rendered
 * @returns the title area or null, if title is not given
 */
export const DrawerTitle = (props) => {
    const dataProps = useDataProps(props);
    const { title } = props;
    if (!hasValue(title)) {
        return null;
    }
    else if (isAvatarTitle(title)) {
        const { text, onClick, avatar: { description, ...avatarProps }, } = title;
        return (_jsxs(Stack, { ...dataProps, gap: 2, alignItems: "center", cursor: onClick ? 'pointer' : undefined, onClick: onClick ? onClick : undefined, children: [_jsx(Avatar, { ...avatarProps, "data-e2e-id": DrawerTestAttributes.titleAvatar }), _jsxs(Stack, { flexDirection: "column", children: [_jsx(Text, { variant: "body", "data-e2e-id": DrawerTestAttributes.titleText, children: text }), _jsx(Text, { variant: "body", color: "n300", "data-e2e-id": DrawerTestAttributes.titleDescription, children: description })] })] }));
    }
    const { text, description, onClick, icon } = title;
    return (_jsxs(Stack, { ...dataProps, gap: 2, alignItems: "center", overflow: "hidden", cursor: onClick ? 'pointer' : undefined, onClick: onClick ? onClick : undefined, children: [icon ? _jsx(Icon, { ...icon, "data-e2e-id": DrawerTestAttributes.titleIcon }) : null, _jsxs(Stack, { flexDirection: "column", minWidth: 0, children: [_jsx(Text, { variant: "h5", "data-e2e-id": DrawerTestAttributes.titleText, ellipsis: true, children: text }), _jsx(Text, { variant: "body", ellipsis: true, color: "n300", "data-e2e-id": DrawerTestAttributes.titleDescription, children: description })] })] }));
};
const titleAreaPadding = {
    base: 3,
    tabletPortrait: 6,
};
export const Drawer = ({ open, title, actions, loading, titleSkeleton, maxWidth, aboveModals = false, children, ...rest }) => {
    const dataProps = useDataProps(rest);
    const onCloseClick = React.useCallback(() => { var _a; return (_a = rest.onClose) === null || _a === void 0 ? void 0 : _a.call(rest, {}, 'backdropClick'); }, [rest]);
    const { isMobileLandscapeAndLarger } = useBreakpoints();
    // Announce this right-anchored drawer so Sidekick (and any other surface that
    // cares) can step aside — e.g. minimize into a floating popup — instead of
    // covering it. Drawer is hardcoded to anchor="right", so every usage qualifies.
    useRegisterSideDrawer(Boolean(open));
    // Only drop MUI's focus trap while a floating popup is actually mounted
    // (e.g. Sidekick's minimized popup). Otherwise keyboard users would be able
    // to tab out of every drawer in the app into aria-hidden siblings — we want
    // that relaxation only when an adjacent interactive surface needs focus.
    const isFloatingPopupOpen = useIsFloatingPopupOpen();
    const paperProps = React.useMemo(() => {
        return {
            sx: {
                paddingTop: `env(safe-area-inset-top)`,
                paddingBottom: 'env(safe-area-inset-bottom)',
                boxSizing: 'border-box',
            },
        };
    }, []);
    const modalProps = React.useMemo(() => ({ disableEnforceFocus: isFloatingPopupOpen }), [isFloatingPopupOpen]);
    return (_jsxs(MuiDrawer, { open: open, ...rest, "data-e2e-id": DrawerTestAttributes.drawer, ...dataProps, anchor: "right", PaperProps: paperProps, ModalProps: modalProps, 
        // Exactly the modal layer, not above it: menus and selects inside the drawer also open at that
        // layer, and whatever mounts later wins at equal z-index, so they keep appearing in front.
        sx: aboveModals ? theme => ({ zIndex: theme.zIndex.modal }) : undefined, children: [loading && titleSkeleton ? (_jsx(_Fragment, { children: titleSkeleton })) : (_jsxs(Stack, { justifyContent: "space-between", alignItems: "center", flexDirection: "row-reverse", padding: titleAreaPadding, gap: 2, maxWidth: maxWidth, width: isMobileLandscapeAndLarger ? '100%' : '100vw', minWidth: isMobileLandscapeAndLarger ? '472px' : '100%', boxSizing: "border-box", children: [_jsxs(Stack, { alignItems: "center", gap: 1, children: [actions, _jsx(Button, { icon: "close", onClick: onCloseClick, "aria-label": i18next.t($ => $.mf.navigation_drawer.src.translations.json.closeAriaLabel), size: "compact", variant: "tertiary" })] }), _jsx(DrawerTitle, { title: title, "data-e2e-id": DrawerTestAttributes.title })] })), children] }));
};
